import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import EligibilityCheck from './pages/EligibilityCheck';
import AuthenticatedEligibilityCheck from './pages/AuthenticatedEligibilityCheck';
import AuthenticatedNewFacility from './pages/AuthenticatedNewFacility';
import EligibilitySuccess from './pages/EligibilitySuccess';
import DealOptions from './pages/DealOptions';
import AdvanceDraw from './components/features/advance/AdvanceDraw';
import FinalConfirmation from './components/features/facility/FinalConfirmation';
import Questionnaire from './components/features/questionnaire/Questionnaire';
import Dashboard from './pages/Dashboard';
// import AdminPanel from './pages/AdminPanel';
import AdminDashboard from './pages/AdminDashboard';
import NewFacility from './pages/NewFacility';
import UserManagement from './components/features/user/UserManagement';
import TradeManagement from './components/features/trade/TradeManagement';
import Login from './pages/Login';
import ResetPassword from './components/features/auth/ResetPassword';
import ExerciseNotice from './pages/ExerciseNotice'; // Used in routes
import Blog from './pages/Blog';
import Careers from './pages/Careers';
import SocialImpact from './components/features/impact/SocialImpact';
import Glossary from './pages/Glossary';
import FAQ from './pages/FAQ';
import LegalPage from './pages/LegalPage';
import CookieConsent from './components/common/CookieConsent';
import BrokerDashboard from './pages/BrokerDashboard';
import BrokerRegistration from './pages/BrokerRegistration';
import BrokerEligibility from './pages/BrokerEligibility';
import BrokerInvite from './pages/BrokerInvite';
import Contact from './pages/Contact';
import CustomerService from './pages/CustomerService';
import ScrollToTop from './components/common/ScrollToTop';
import PrivacyPolicy from './components/features/privacy/PrivacyPolicy';
import TermsAndConditions from './components/features/legal/TermsAndConditions';
import CookiePolicy from './pages/CookiePolicy';
import Disclaimer from './pages/Disclaimer';
import TermSheetViewer from './components/features/termsheet/TermSheetViewer';
import PreClosingChecklist from './pages/PreClosingChecklist';
import PreClosingChecklistRedesigned from './components/features/preClosing/PreClosingChecklistRedesigned';
import DealDocuments from './pages/DealDocuments';
import { ChecklistProvider } from './context/ChecklistContext';
import './App.css';

interface AppState {
  eligibilityResults: any;
  selectedCommitment: any;
  finalSelection: any;
  facilityId: string | null;
}

function App() {
  const [appState, setAppState] = useState<AppState>({
    eligibilityResults: null,
    selectedCommitment: null,
    finalSelection: null,
    facilityId: null
  });

  const updateAppState = (updates: Partial<AppState>) => {
    setAppState(prev => ({ ...prev, ...updates }));
  };

  return (
    <ChecklistProvider>
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/check-eligibility" element={<EligibilityCheck />} />
        <Route path="/new-facility-check" element={<AuthenticatedNewFacility />} />
        <Route 
          path="/eligibility-success" 
          element={
            <EligibilitySuccess 
              results={appState.eligibilityResults}
              onUpdateState={updateAppState}
            />
          } 
        />
        <Route 
          path="/deal-options" 
          element={
            <DealOptions 
              eligibilityResults={appState.eligibilityResults}
              onUpdateState={updateAppState}
            />
          } 
        />
        <Route 
          path="/advance-draw" 
          element={
            <AdvanceDraw 
              selectedCommitment={appState.selectedCommitment}
              eligibilityResults={appState.eligibilityResults}
              onUpdateState={updateAppState}
            />
          } 
        />
        <Route 
          path="/confirmation" 
          element={
            <FinalConfirmation 
              finalSelection={appState.finalSelection}
              onUpdateState={updateAppState}
            />
          } 
        />
        <Route 
          path="/questionnaire" 
          element={
            <Questionnaire 
              facilityId={appState.facilityId}
              companyName={appState.finalSelection?.companyName || appState.eligibilityResults?.companyName}
              companyInfo={appState.eligibilityResults?.data?.companyInfo}
              secInfo={appState.eligibilityResults?.data?.secInfo}
              stockData={appState.eligibilityResults?.data?.stockData}
              onUpdateState={updateAppState}
            />
          } 
        />
        <Route 
          path="/questionnaire/:selectionId" 
          element={
            <Questionnaire 
              facilityId={appState.facilityId}
              companyName={appState.finalSelection?.companyName || appState.eligibilityResults?.companyName}
              companyInfo={appState.eligibilityResults?.data?.companyInfo}
              secInfo={appState.eligibilityResults?.data?.secInfo}
              stockData={appState.eligibilityResults?.data?.stockData}
              onUpdateState={updateAppState}
            />
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            <Dashboard 
              facilityId={appState.facilityId}
            />
          } 
        />
        <Route 
          path="/pre-closing-checklist" 
          element={<PreClosingChecklistRedesigned />} 
        />
        <Route 
          path="/deal-documents" 
          element={<DealDocuments />} 
        />
        <Route 
          path="/admin" 
          element={<AdminDashboard />} 
        />
        {/* <Route 
          path="/admin-old" 
          element={<AdminPanel />} 
        /> */}
        <Route 
          path="/new-facility" 
          element={<NewFacility />} 
        />
        <Route 
          path="/settings/users" 
          element={<UserManagement />} 
        />
        <Route 
          path="/admin/trades" 
          element={<TradeManagement />} 
        />
        <Route 
          path="/login" 
          element={<Login />} 
        />
        <Route 
          path="/reset-password" 
          element={<ResetPassword />} 
        />
        <Route 
          path="/exercise/:type" 
          element={<ExerciseNotice />} 
        />
        <Route 
          path="/blog" 
          element={<Blog />} 
        />
        <Route 
          path="/blog/:slug" 
          element={<Blog />} 
        />
        <Route 
          path="/careers" 
          element={<Careers />} 
        />
        <Route 
          path="/careers/:slug" 
          element={<Careers />} 
        />
        <Route 
          path="/social-impact" 
          element={<SocialImpact />} 
        />
        <Route 
          path="/glossary" 
          element={<Glossary />} 
        />
        <Route 
          path="/faq" 
          element={<FAQ />} 
        />
        <Route 
          path="/contact" 
          element={<Contact />} 
        />
        <Route 
          path="/customer-service" 
          element={<CustomerService />} 
        />
        <Route 
          path="/privacy" 
          element={<PrivacyPolicy />} 
        />
        <Route 
          path="/legal/privacy" 
          element={<PrivacyPolicy />} 
        />
        <Route 
          path="/terms" 
          element={<TermsAndConditions />} 
        />
        <Route 
          path="/legal/terms" 
          element={<TermsAndConditions />} 
        />
        <Route 
          path="/cookies" 
          element={<CookiePolicy />} 
        />
        <Route 
          path="/legal/cookies" 
          element={<CookiePolicy />} 
        />
        <Route 
          path="/disclaimer" 
          element={<Disclaimer />} 
        />
        <Route 
          path="/legal/disclaimer" 
          element={<Disclaimer />} 
        />
        <Route 
          path="/legal/:type" 
          element={<LegalPage />} 
        />
        
        {/* Broker Routes */}
        <Route 
          path="/broker/register" 
          element={<BrokerRegistration />} 
        />
        <Route 
          path="/broker/dashboard" 
          element={<BrokerDashboard />} 
        />
        <Route 
          path="/broker/eligibility/:symbol" 
          element={<BrokerEligibility />} 
        />
        <Route 
          path="/broker-invite/:token" 
          element={<BrokerInvite />} 
        />
        
        {/* Term Sheet Viewer */}
        <Route 
          path="/term-sheet/:selectionId" 
          element={<TermSheetViewer />} 
        />
        
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <CookieConsent />
    </Router>
    </ChecklistProvider>
  );
}

export default App; 